import { capitalize } from "lodash";
import { z } from "zod";

import {
  ParamReference,
  SourceLocation,
  SourceRange,
} from "./cellReferencesV1.js";

// begin -- keep in sync with jinjasql/sqlsafe.py

export const CaseTransform = z.object({
  type: z.enum(["lower", "upper", "capitalize"]),
});
export type CaseTransform = z.infer<typeof CaseTransform>;

export const ReplaceTransform = z.object({
  type: z.literal("replace"),
  old: z.string(),
  new: z.string(),
});
export type ReplaceTransform = z.infer<typeof ReplaceTransform>;

export const SimpleTransform = z.union([CaseTransform, ReplaceTransform]);
export type SimpleTransform = z.infer<typeof SimpleTransform>;

export const CompositeTransform = z.object({
  type: z.literal("composite"),
  transforms: z.array(SimpleTransform),
});
export type CompositeTransform = z.infer<typeof CompositeTransform>;

export const KnownTransform = z.union([SimpleTransform, CompositeTransform]);
export type KnownTransform = z.infer<typeof KnownTransform>;

export function applyTransform(
  value: string,
  transform?: KnownTransform,
): string {
  if (transform == null) {
    return value;
  }
  switch (transform.type) {
    case "lower":
      return value.toLowerCase();
    case "upper":
      return value.toUpperCase();
    case "capitalize":
      return capitalize(value);
    case "replace":
      return value.replaceAll(transform.old, transform.new);
    case "composite":
      return transform.transforms.reduce(applyTransform, value);
  }
}

export const UnknownTransform = z.object({
  type: z.literal("unknown"),
});
export type UnknownTransform = z.infer<typeof UnknownTransform>;

export const SqlSafeTransform = z.union([KnownTransform, UnknownTransform]);
export type SqlSafeTransform = z.infer<typeof SqlSafeTransform>;

export const SqlSafeOutput = z.object({
  name: z.string(),
  range: SourceRange,
  transform: SqlSafeTransform.optional(),
});
export type SqlSafeOutput = z.infer<typeof SqlSafeOutput>;

// end -- keep in sync with jinjasql/sqlsafe.py

// begin -- keep in sync with jinjasql/parse.py

export const SqlSafeExpression = z.object({
  range: SourceRange,
  outputs: z.array(SqlSafeOutput),
});
export type SqlSafeExpression = z.infer<typeof SqlSafeExpression>;

export const SqlSafeTableReference = z.object({
  type: z.literal("sqlsafe"),
  index: z.number(),
});
export type SqlSafeTableReference = z.infer<typeof SqlSafeTableReference>;

export const StaticTableReference = z.object({
  type: z.literal("static"),
  name: z.string(),
  // Each element of locations maps to a full reference to `name`, but that
  // reference may be split up across multiple source locations.
  locations: z.array(z.array(SourceLocation)),
});
export type StaticTableReference = z.infer<typeof StaticTableReference>;

export const DynamicTableReference = z.object({
  type: z.literal("dynamic"),
  segments: z.array(z.union([StaticTableReference, SqlSafeTableReference])),
});
export type DynamicTableReference = z.infer<typeof DynamicTableReference>;

export const TableReference = z.union([
  StaticTableReference,
  DynamicTableReference,
]);
export type TableReference = z.infer<typeof TableReference>;

export const JinjaSqlReferences = z.object({
  jinjaReferencedParams: z.array(ParamReference),
  sqlsafe: z.array(SqlSafeExpression),
  tables: z.array(TableReference),
  singleStatement: z.boolean(),
  onlySelects: z.boolean(),
  parseError: z.union([z.null(), z.string()]),
});

// end -- keep in sync with jinjasql/parse.py
